import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { invalidateEntity } from '../cache-invalidation';
import { orgFunctionKeys } from '../query-keys';
import {
  addOrgFunctionContract,
  updateOrgFunctionContract,
  deleteOrgFunctionContract,
  type AddOrgFunctionRequest,
  type AddOrgFunctionResponse,
  type UpdateOrgFunctionRequest,
  type UpdateOrgFunctionResponse,
  type DeleteOrgFunctionResponse
} from '@bliks-io/contracts';

/**
 * Create OrgFunction mutation
 *
 * Creates a new OrgFunction (team or role) via the unified orgFunction endpoint.
 * - Invalidates every query family that may hold a populated copy — creating
 *   with a `lead` changes what that user holds (Meine Funktionen)
 * - Returns the created OrgFunction
 *
 * Only the list refetch is awaited, so `mutateAsync` resolves as soon as
 * callers can render the new entity by id; the wider dependent fan-out (which
 * includes families as busy as processes and tasks) settles on its own.
 */
export function useCreateOrgFunction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddOrgFunctionRequest): Promise<AddOrgFunctionResponse> => {
      const { data: response } = await apiClient.post<AddOrgFunctionResponse>(
        addOrgFunctionContract.path,
        data
      );
      return response;
    },
    onSuccess: () => {
      void invalidateEntity(queryClient, 'orgFunction');
      // `cancelRefetch: false` joins the refetch the line above already
      // started instead of restarting it.
      return queryClient.invalidateQueries(
        { queryKey: orgFunctionKeys.lists() },
        { cancelRefetch: false }
      );
    }
  });
}

/**
 * Update OrgFunction mutation
 *
 * Updates an existing OrgFunction by ID.
 * - Invalidates every query family that may hold a populated copy
 * - Returns the updated OrgFunction
 */
export function useUpdateOrgFunction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: string;
      data: UpdateOrgFunctionRequest;
    }): Promise<UpdateOrgFunctionResponse> => {
      const path = updateOrgFunctionContract.path.replace(':id', id);
      const { data: response } = await apiClient.patch<UpdateOrgFunctionResponse>(path, data);
      return response;
    },
    onSuccess: () => {
      invalidateEntity(queryClient, 'orgFunction');
    }
  });
}

/**
 * Delete OrgFunction mutation
 *
 * Soft-deletes a OrgFunction by ID.
 * - Invalidates the OrgFunctions list cache (and dependents)
 * - Removes the OrgFunction detail from cache
 */
export function useDeleteOrgFunction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<DeleteOrgFunctionResponse> => {
      const path = deleteOrgFunctionContract.path.replace(':id', id);
      const { data } = await apiClient.delete<DeleteOrgFunctionResponse>(path);
      return data;
    },
    onSuccess: (_, id) => {
      invalidateEntity(queryClient, 'orgFunction');
      queryClient.removeQueries({ queryKey: orgFunctionKeys.detail(id) });
    }
  });
}
